'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["slots", "slotProps", "className", "classes", "sx", "messageId", "children", "groupAuthorName"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageRoot, useChatVariant, useMessage } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { mergeSlotProps, resolveSlotProps } from "../internals/mergeSlotProps.mjs";
import { useChatMessageUtilityClasses } from "./chatMessageClasses.mjs";
import { ChatMessageError } from "../ChatMessageError/ChatMessageError.mjs";
import { ChatMessageAvatar } from "./ChatMessageAvatar.mjs";
import { ChatMessageContent } from "./ChatMessageContent.mjs";
import { ChatMessageMeta } from "./ChatMessageMeta.mjs";
import { ChatMessageInlineMeta } from "./ChatMessageInlineMeta.mjs";
import { ChatMessageActions } from "./ChatMessageActions.mjs";
import { ChatStreamingIndicator } from "../ChatIndicators/ChatStreamingIndicator.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessage');

/**
 * Message context passed to a function-valued `slotProps.actions` (and the flat
 * `slotProps.messageActions`), so a consumer can return per-message action props
 * — most commonly `extraActions` for assistant rows.
 */

const ChatMessageStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles.root, props.ownerState?.role === 'user' && styles.roleUser, props.ownerState?.role === 'assistant' && styles.roleAssistant]
})(({
  theme,
  ownerState
}) => {
  const isCompact = ownerState?.variant === 'compact';
  const isOwnMessage = ownerState?.isOwnMessage ?? false;
  const densityPaddingBlock = {
    compact: theme.spacing(0.25),
    standard: theme.spacing(1),
    comfortable: theme.spacing(2)
  };
  const paddingBottom = densityPaddingBlock[ownerState?.density ?? 'standard'];

  // The article is the message list's roving tabindex item, so it needs a
  // visible keyboard focus indicator (WCAG 2.4.7). Inset so the ring is not
  // clipped by the scroller's overflow.
  const focusRing = {
    '&:focus-visible': {
      outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
      outlineOffset: -2,
      borderRadius: theme.shape.borderRadius
    }
  };
  if (isCompact) {
    // Compact: avatar and author name share the first row, content below.
    // For grouped messages (not first in group) the avatar column stays for alignment
    // but no avatar/author name is rendered.
    const isGrouped = ownerState?.isGrouped;
    return _extends({
      display: 'grid',
      columnGap: theme.spacing(1),
      width: '100%',
      boxSizing: 'border-box',
      paddingInline: theme.spacing(2),
      paddingBlock: `0 ${paddingBottom}`,
      fontFamily: theme.typography.fontFamily
    }, focusRing, isGrouped ? {
      gridTemplateColumns: 'var(--MuiChatMessage-avatarSize) 1fr auto',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: '". content meta" ". error ."'
    } : {
      gridTemplateColumns: 'var(--MuiChatMessage-avatarSize) 1fr auto',
      gridTemplateRows: 'auto auto auto',
      gridTemplateAreas: '"avatar authorName meta" "avatar content ." ". error ."'
    }, {
      // Avatar-less layout: collapse the reserved avatar grid track so the bubble
      // and meta lane reclaim the row. Applies to both grouped and first-in-group.
      '&.MuiChatMessage-noAvatar': {
        gridTemplateColumns: '1fr auto',
        gridTemplateAreas: isGrouped ? '"content meta" "error ."' : '"authorName meta" "content ." "error ."'
      }
    });
  }
  const isGrouped = ownerState?.isGrouped;

  // Default variant: two-row grid. First row holds avatar + content bubble,
  // second row holds message actions below the bubble, aligned to the avatar side.
  // Meta is rendered inline inside the bubble via ChatMessageInlineMeta.
  return _extends({
    display: 'grid',
    gridTemplateColumns: isGrouped ? 'var(--MuiChatMessage-avatarSize) 1fr' : 'auto 1fr',
    gridTemplateRows: 'auto auto auto',
    gridTemplateAreas: isGrouped ? '". content" ". error" ". actions"' : '"avatar content" ". error" ". actions"',
    columnGap: theme.spacing(0.5),
    width: '100%',
    boxSizing: 'border-box',
    paddingInline: theme.spacing(2),
    // Phantom column: reserve the same width as the avatar on the opposite side so
    // assistant and user bubbles always share the same horizontal content lane.
    paddingInlineEnd: `calc(${theme.spacing(2)} + var(--MuiChatMessage-avatarSize))`,
    paddingBlock: `0 ${paddingBottom}`,
    // When actions are present, their row already provides visual spacing,
    // so reduce the bottom padding to avoid double spacing between messages.
    '&:has(.MuiChatMessage-actions)': {
      paddingBlockEnd: theme.spacing(0.25)
    },
    fontFamily: theme.typography.fontFamily
  }, focusRing, isOwnMessage && {
    gridTemplateColumns: isGrouped ? '1fr var(--MuiChatMessage-avatarSize)' : '1fr auto',
    gridTemplateAreas: isGrouped ? '"content ." "error ." "actions ."' : '"content avatar" "error ." "actions ."',
    paddingInlineStart: `calc(${theme.spacing(2)} + var(--MuiChatMessage-avatarSize))`,
    paddingInlineEnd: theme.spacing(2)
  }, {
    // Avatar-less layout: collapse the reserved avatar column and drop the phantom
    // padding so the bubble fills the full content lane on both sides.
    '&.MuiChatMessage-noAvatar': {
      gridTemplateColumns: '1fr',
      gridTemplateAreas: '"content" "error" "actions"',
      paddingInlineStart: theme.spacing(2),
      paddingInlineEnd: theme.spacing(2)
    }
  });
});
const ChatMessage = /*#__PURE__*/React.forwardRef(function ChatMessage(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessage'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx,
      messageId,
      children,
      groupAuthorName
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatMessageUtilityClasses(classesProp);
  const message = useMessage(messageId);
  const variant = useChatVariant();
  const isCompact = variant === 'compact';
  const stateClasses = clsx({
    'user': classes.roleUser,
    'assistant': classes.roleAssistant
  }[message?.role], {
    'streaming': classes.streaming,
    'error': classes.error
  }[message?.status]);

  // Slot resolution: `null` means "hide and adjust layout"; `undefined` means "use default".
  const AvatarSlot = slots?.avatar;
  const hasAvatar = AvatarSlot !== null;

  // The error surface is resolved through the `error` slot in both branches so custom
  // message composition via `children` can still customize (or keep consistent) the
  // error rendering, matching the slot-driven path below. `null` hides it entirely.
  const hasError = slots?.error !== null;
  const ErrorComp = slots?.error ?? ChatMessageError;

  // Build the inner tree from slots when no `children` were passed (slot-driven).
  // When a consumer provides `children`, render them as-is for backward-compatible
  // custom composition — in every variant, including compact. The compact author
  // label is delivered separately via `groupAuthorName` (see headless MessageGroup),
  // so it never conflates with a consumer's children.
  let innerTree;
  if (children !== undefined) {
    innerTree = /*#__PURE__*/_jsxs(React.Fragment, {
      children: [children, hasError && /*#__PURE__*/_jsx(ErrorComp, _extends({}, slotProps?.error ?? {}))]
    });
  } else {
    const ContentComp = slots?.content ?? ChatMessageContent;
    const MetaSlot = slots?.meta;
    const InlineMetaSlot = slots?.inlineMeta;
    const ActionsSlot = slots?.actions;
    const AvatarComp = AvatarSlot ?? ChatMessageAvatar;
    const isStreaming = message?.status === 'streaming';
    const hasMeta = Boolean(message?.createdAt) || Boolean(message?.editedAt) || Boolean(message?.status);
    const InlineMetaComp = InlineMetaSlot ?? ChatMessageInlineMeta;
    const inlineMeta = !isCompact && !isStreaming && hasMeta && InlineMetaSlot !== null ? /*#__PURE__*/React.createElement(InlineMetaComp, slotProps?.inlineMeta ?? {}) : undefined;
    const MetaComp = MetaSlot ?? ChatMessageMeta;
    const externalMeta = isCompact && MetaSlot !== null ? /*#__PURE__*/_jsx(MetaComp, _extends({}, slotProps?.meta ?? {})) : null;

    // Resolve the actions slotProps against the message context — the same
    // place the dateDivider/unreadMarker precedent resolves row-level function
    // slotProps. The documented `({ message }) => ({ extraActions })` recipe
    // works verbatim because `message` is the first key of the context.
    const actionsContext = {
      message,
      messageId,
      role: message?.role,
      status: message?.status,
      streaming: message?.status === 'streaming'
    };
    const resolvedActionsProps = resolveSlotProps(slotProps?.actions ?? {}, actionsContext);
    const hasExtraActions = (resolvedActionsProps.extraActions?.length ?? 0) > 0;

    // Mounted only on the actively streaming assistant message so a single
    // store subscriber exists per list, not one per row. Disabling the
    // feature arrives as `slots.streamingIndicator === null` (the row
    // pipeline nulls the slot), so no mode needs to be threaded here.
    const StreamingIndicatorSlot = slots?.streamingIndicator;
    const streamingIndicator = isStreaming && message?.role === 'assistant' && StreamingIndicatorSlot !== null ? /*#__PURE__*/React.createElement(StreamingIndicatorSlot ?? ChatStreamingIndicator, _extends({
      message
    }, resolveSlotProps(slotProps?.streamingIndicator ?? {}, actionsContext))) : undefined;
    innerTree = /*#__PURE__*/_jsxs(React.Fragment, {
      children: [groupAuthorName, hasAvatar && /*#__PURE__*/_jsx(AvatarComp, _extends({}, slotProps?.avatar ?? {})), /*#__PURE__*/_jsx(ContentComp, _extends({
        afterContent: streamingIndicator !== undefined || inlineMeta !== undefined ? /*#__PURE__*/_jsxs(React.Fragment, {
          children: [streamingIndicator, inlineMeta]
        }) : undefined
      }, slotProps?.content ?? {})), externalMeta, hasError && /*#__PURE__*/_jsx(ErrorComp, _extends({}, slotProps?.error ?? {})), ActionsSlot !== null && (ActionsSlot || hasExtraActions) && /*#__PURE__*/_jsx(ChatMessageActions, _extends({
        message: message
      }, resolvedActionsProps, {
        children: ActionsSlot ? /*#__PURE__*/_jsx(ActionsSlot, {
          messageId: messageId
        }) : null
      }))]
    });
  }
  return /*#__PURE__*/_jsx(MessageRoot, _extends({
    ref: ref,
    messageId: messageId
  }, other, {
    slots: {
      root: slots?.root ?? ChatMessageStyled
    },
    slotProps: {
      root: mergeSlotProps({
        className: clsx(classes.root, stateClasses, className, !hasAvatar && classes.noAvatar),
        sx
      }, slotProps?.root)
    },
    children: innerTree
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessage.displayName = "ChatMessage";
process.env.NODE_ENV !== "production" ? ChatMessage.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * @ignore
   * Internal: the group's author label, injected by the headless `MessageGroup`
   * in compact mode so it can share the message's CSS grid. Not part of a
   * consumer's custom composition (which uses `children`).
   */
  groupAuthorName: PropTypes.node,
  isGrouped: PropTypes.bool,
  messageId: PropTypes.string.isRequired,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatMessage };